import { Component, OnInit, inject } from '@angular/core';
import { Player } from '../../../models';
import { ActivatedRoute } from '@angular/router';
import { PlayerService } from '../../../services';
import { LoggingService } from '../../../services/shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { AuditInfoComponent } from '../../shared/audit-info/audit-info.component';
import { RefService } from '../../../services/reference/reference.service';
import { Country } from '../../../models/reference/country';

import { State } from '../../../models/reference/state';
import { PlayerType } from '../../../models/playerType';

type PlayerSnapshot = {
  wwgrId?: number;
  isActive?: boolean;
  shortName: string;
  playFirstName: string;
  playMiddleName: string;
  playLastName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  city: string;
  countryId: number;
  countryName: string;
  stateId: number;
  stateName: string;
  hometown: string;
};

@Component({
  selector: 'app-player-detail',
  standalone: true,
  imports: [FormsModule, AuditInfoComponent],
  templateUrl: './player-detail.component.html',
  styleUrl: './player-detail.component.scss',
})
export class PlayerDetailComponent implements OnInit {
  private loggingService = inject(LoggingService);
  
  // Resolved player
  playerId!: string;
  player: Player = {} as Player;
  countries: Country[] = [];
  states: State[] = [];
  playerTypes: PlayerType[] = [];
  private playerBaseline?: PlayerSnapshot;
  private playerBaselineSignature = '';
  isPlayerChanged = false;
  isSaving = false;

  constructor(
    private route: ActivatedRoute,
    private playerService: PlayerService,
    private toastrService: ToastrService,
    private refService: RefService
  ) { }

  ngOnInit() {
    // Use resolver-provided player data
    this.route.data.subscribe(({ player }) => {
      this.player = player;
      this.playerId = player.playerId!;
      this.getCountries();
      this.getStates();
      this.getPlayerTypes();
      this.capturePlayerBaseline();
    });
  }

  private getCountries(): void {
    this.refService.getCountries().subscribe((countries) => {
      this.countries = countries;
    });
  }

  private getStates(): void {
    this.refService.getStates().subscribe((states) => {
      this.states = states;
    });
  }

  private getPlayerTypes(): void {
    this.playerService.getPlayerTypes().subscribe((playerTypes) => {
      this.playerTypes = playerTypes;

      if (this.playerId) {
        this.playerService
          .getPlayerTypesByPlayer(this.playerId)
          .subscribe((playerSpecificTypes) => {
            this.playerTypes = this.playerTypes.map((type) => ({
              ...type,
              selected: playerSpecificTypes.some(
                (specificType) =>
                  specificType.playerTypeId === type.playerTypeId
              ),
            }));
          });
      }
    });
  }

  save(): void {
    if (!this.canSave) {
      this.toastrService.info('No changes to save.', 'Info');
      return;
    }

    this.isSaving = true;
    const previousModifiedOnUtc = this.player?.modifiedOnUtc;

    this.playerService.updatePlayer(this.player).subscribe({
      next: (player) => {
        if (previousModifiedOnUtc !== player.modifiedOnUtc) {
          this.toastrService.success('Player saved', 'Success');
        }
        this.player = { ...player } as Player;
        this.capturePlayerBaseline();
        this.isSaving = false;
      },
      error: (error) => {
        this.isSaving = false;
        this.toastrService.error('Error saving player details', 'Failure');
        this.loggingService.error('Error saving player:', error);
      },
    });
  }

  onPlayerFieldChanged(): void {
    this.evaluatePlayerChanges();
  }

  get canSave(): boolean {
    return this.isPlayerChanged && !this.isSaving;
  }

  private capturePlayerBaseline(): void {
    this.playerBaseline = this.buildPlayerSnapshot(this.player);
    this.playerBaselineSignature = this.snapshotSignature(this.playerBaseline);
    this.isPlayerChanged = false;
  }

  private evaluatePlayerChanges(): void {
    const currentSnapshot = this.buildPlayerSnapshot(this.player);
    this.isPlayerChanged = this.snapshotSignature(currentSnapshot) !== this.playerBaselineSignature;
  }

  private buildPlayerSnapshot(player?: Player): PlayerSnapshot {
    return {
      wwgrId: this.normalizeNumber(player?.wwgrId),
      isActive: player?.isActive ?? false,
      shortName: this.normalizeText(player?.shortName),
      playFirstName: this.normalizeText(player?.playFirstName),
      playMiddleName: this.normalizeText(player?.playMiddleName),
      playLastName: this.normalizeText(player?.playLastName),
      firstName: this.normalizeText(player?.firstName),
      middleName: this.normalizeText(player?.middleName),
      lastName: this.normalizeText(player?.lastName),
      city: this.normalizeText(player?.city),
      countryId: Number(player?.countryId) || 0,
      countryName: this.normalizeText(player?.countryName),
      stateId: Number(player?.stateId) || 0,
      stateName: this.normalizeText(player?.stateName),
      hometown: this.normalizeText(player?.hometown),
    };
  }

  private snapshotSignature(snapshot?: PlayerSnapshot): string {
    return JSON.stringify(snapshot ?? {});
  }

  private normalizeNumber(value: unknown): number | undefined {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  private normalizeText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }
}
